import { useSyncExternalStore } from "react";
import { getSession, onSession } from "@/api/auth";
import type { Me } from "@/api/types";

export function useSession() {
  return useSyncExternalStore(
    (cb) => {
      const off = onSession(cb);
      return () => {
        off();
      };
    },
    getSession,
    getSession,
  );
}

export function useMe(): Me {
  const s = useSession();
  if (!s) throw new Error("useMe outside a signed-in page");
  return s.user;
}

const LAST_NP = "rs.lastNewspaper";
const RECENT = "rs.recentReels";

export function lastNewspaper(): string | null {
  try {
    return localStorage.getItem(LAST_NP);
  } catch {
    return null;
  }
}

export function rememberNewspaper(id: string) {
  try {
    localStorage.setItem(LAST_NP, id);
  } catch {
    /* private mode */
  }
}

export type RecentReel = { id: string; label: string; np: string };

export function recentReels(): RecentReel[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT) || "[]");
  } catch {
    return [];
  }
}

export function rememberReel(r: RecentReel) {
  try {
    const list = [r, ...recentReels().filter((x) => x.id !== r.id)].slice(0, 5);
    localStorage.setItem(RECENT, JSON.stringify(list));
    window.dispatchEvent(new Event("rs.recent"));
  } catch {
    /* private mode */
  }
}

export function forgetReel(id: string) {
  try {
    localStorage.setItem(RECENT, JSON.stringify(recentReels().filter((x) => x.id !== id)));
    window.dispatchEvent(new Event("rs.recent"));
  } catch {
    /* private mode */
  }
}

export type NewspaperRole = "super" | "admin" | "member";

export const ROLE_LABEL: Record<NewspaperRole, string> = { super: "Super admin", admin: "Admin", member: "Member" };

/** The signed-in person's role on a newspaper ('super' for super admins), or null without access. */
export function useNewspaperRole(npId: string | null | undefined): NewspaperRole | null {
  const s = useSession();
  if (!s || !npId) return null;
  if (s.user.role === "admin") return "super";
  return (s.user.newspapers.find((n) => n.id === npId)?.my_role as NewspaperRole | undefined) ?? null;
}

export function useCanManage(npId: string | null | undefined): boolean {
  const r = useNewspaperRole(npId);
  return r === "super" || r === "admin";
}
